import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../../firebaseClient";

export type AiAnalysisLogInput = {
  module?: string;
  mode?: string;
  files: Array<{ name: string; type?: string; size?: number }>;
  result: any;
};

async function sha256Hex(text: string): Promise<string> {
  try {
    // Browser / modern runtimes
    const enc = new TextEncoder();
    const buf = await crypto.subtle.digest("SHA-256", enc.encode(text));
    const arr = Array.from(new Uint8Array(buf));
    return arr.map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return "";
  }
}

function safeText(v: any): string {
  return typeof v === "string" ? v : "";
}

/**
 * AI 產出留痕 / 可追溯：每次分析存一筆（時間、使用者、檔名、摘要、風險、hash/版本）
 * - Best effort：寫入失敗不應阻斷主流程。
 * - 資料最小化：不存整份原文；只存摘要/風險/輸出結果（可視需求再縮減）。
 */
export async function saveAiAnalysisLog(input: AiAnalysisLogInput): Promise<void> {
  const user = auth.currentUser;
  const userEmail = user?.email || "";
  const userUid = user?.uid || "";

  const summary = safeText(input?.result?.summary || "");
  const riskLevel = safeText(
    input?.result?.sfRiskBenefit?.riskLevel ||
      input?.result?.overallRisk?.level ||
      input?.result?.overallRiskLevel ||
      ""
  );

  // 以 summary + 模式 + 檔名做一個穩定 hash（避免存原文）
  const basis = JSON.stringify({
    module: input?.module || "",
    mode: input?.mode || "",
    files: input?.files?.map((f) => f?.name) || [],
    summary,
    riskLevel,
  });
  const hash = await sha256Hex(basis);

  const docData = {
    createdAt: serverTimestamp(),
    module: input?.module || "",
    mode: input?.mode || "",
    userEmail,
    userUid,
    files: input?.files || [],
    summary,
    riskLevel,
    basisHash: hash,
    // 保留 AI 結果（可追溯）。如未來要更嚴格最小化，可改存截斷版/或只存關鍵欄位。
    result: input?.result || null,
  };

  await addDoc(collection(db as any, "ai_analysis_logs"), docData as any);
}
